import { useState, useEffect, useCallback } from "react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_META = {
  new: { label: "New", color: "#00FFFF", bg: "rgba(0,255,255,0.1)" },
  contacted: { label: "Contacted", color: "#FFB347", bg: "rgba(255,179,71,0.12)" },
  qualified: { label: "Qualified", color: "#A78BFA", bg: "rgba(167,139,250,0.12)" },
  closed: { label: "Closed", color: "#00DC78", bg: "rgba(0,220,120,0.12)" }
};

const BriefRequestsTab = () => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [editingId, setEditingId] = useState(null);
  const [editNotes, setEditNotes] = useState("");

  const fetchLeads = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/admin/tax-brief/leads`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load");
      const data = await res.json();
      setLeads(data.leads || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchLeads(); }, [fetchLeads]);

  const updateStatus = async (lead, newStatus, notes) => {
    try {
      const res = await fetch(`${API}/admin/tax-brief/leads/${lead.id}`, {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status: newStatus, notes: notes ?? lead.notes ?? "" })
      });
      if (!res.ok) throw new Error("Update failed");
      await fetchLeads();
      setEditingId(null);
    } catch (err) {
      console.error(err);
    }
  };

  const counts = {
    all: leads.length,
    new: leads.filter(l => (l.status || "new") === "new").length,
    contacted: leads.filter(l => l.status === "contacted").length,
    qualified: leads.filter(l => l.status === "qualified").length,
    closed: leads.filter(l => l.status === "closed").length
  };

  const visible = filter === "all" ? leads : leads.filter(l => (l.status || "new") === filter);

  return (
    <div className="dash-tab-content brief-requests-tab" data-testid="brief-requests-tab">
      <div className="brief-header">
        <div>
          <h2>Brief Requests</h2>
          <p className="brief-subtitle">Leads from the public Asset Stabilization Brief form. Follow up within 24 hours.</p>
        </div>
        <button onClick={fetchLeads} className="brief-refresh" data-testid="brief-refresh">
          {loading ? "Loading..." : "Refresh"}
        </button>
      </div>

      <div className="brief-summary-bar">
        {[
          { key: "all", label: "Total", color: "#fff" },
          { key: "new", label: "New", color: "#00FFFF" },
          { key: "contacted", label: "Contacted", color: "#FFB347" },
          { key: "qualified", label: "Qualified", color: "#A78BFA" },
          { key: "closed", label: "Closed", color: "#00DC78" }
        ].map(s => (
          <button
            key={s.key}
            onClick={() => setFilter(s.key)}
            className={`brief-summary-stat ${filter === s.key ? "active" : ""}`}
            style={filter === s.key ? { borderColor: s.color, color: s.color } : {}}
            data-testid={`brief-filter-${s.key}`}
          >
            <span className="brief-stat-val">{counts[s.key]}</span>
            <span className="brief-stat-lbl">{s.label}</span>
          </button>
        ))}
      </div>

      {loading && leads.length === 0 ? (
        <div className="brief-empty">Loading…</div>
      ) : visible.length === 0 ? (
        <div className="brief-empty" data-testid="brief-empty-state">
          {filter === "all"
            ? "No brief requests yet. Once someone fills out the form on your landing page, they'll show up here."
            : `No ${filter} leads yet.`}
        </div>
      ) : (
        <div className="brief-leads-list">
          {visible.map(lead => {
            const status = lead.status || "new";
            const meta = STATUS_META[status] || STATUS_META.new;
            const ts = lead.timestamp ? new Date(lead.timestamp) : null;
            const tsStr = ts ? `${ts.toLocaleDateString()} ${ts.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}` : "";
            return (
              <div key={lead.id} className={`brief-lead-card brief-status-${status}`} data-testid={`brief-lead-${lead.id}`}>
                <div className="brief-lead-top">
                  <div className="brief-lead-name-block">
                    <h3 className="brief-lead-name">{lead.name}</h3>
                    <p className="brief-lead-meta">{tsStr}</p>
                  </div>
                  <span className="brief-status-pill" style={{ color: meta.color, background: meta.bg, borderColor: meta.color + "55" }}>
                    {meta.label}
                  </span>
                </div>

                <div className="brief-lead-grid">
                  <div className="brief-lead-field">
                    <span className="brief-lead-label">Email</span>
                    <a href={`mailto:${lead.email}`} className="brief-lead-link" data-testid={`brief-email-${lead.id}`}>{lead.email}</a>
                  </div>
                  <div className="brief-lead-field">
                    <span className="brief-lead-label">Company</span>
                    <span>{lead.company || "—"}</span>
                  </div>
                  <div className="brief-lead-field">
                    <span className="brief-lead-label">Role</span>
                    <span>{lead.role || "—"}</span>
                  </div>
                  <div className="brief-lead-field">
                    <span className="brief-lead-label">Portfolio</span>
                    <span>{lead.portfolio_units || "—"} units</span>
                  </div>
                </div>

                {editingId === lead.id ? (
                  <div className="brief-edit-row">
                    <textarea
                      value={editNotes}
                      onChange={(e) => setEditNotes(e.target.value)}
                      placeholder="Notes (e.g. follow-up date, conversation summary)…"
                      rows="3"
                      data-testid={`brief-notes-input-${lead.id}`}
                    />
                    <div className="brief-edit-actions">
                      <button onClick={() => updateStatus(lead, status, editNotes)} className="brief-btn brief-btn-primary" data-testid={`brief-save-notes-${lead.id}`}>Save Notes</button>
                      <button onClick={() => setEditingId(null)} className="brief-btn brief-btn-secondary">Cancel</button>
                    </div>
                  </div>
                ) : lead.notes ? (
                  <div className="brief-notes-display" data-testid={`brief-notes-${lead.id}`}>
                    <strong>Notes:</strong> {lead.notes}
                  </div>
                ) : null}

                <div className="brief-lead-actions">
                  <a
                    href={`mailto:${lead.email}?subject=${encodeURIComponent("Your Asset Stabilization Brief — Moovin Forward")}&body=${encodeURIComponent(`Hi ${(lead.name || "").split(" ")[0]},\n\nThank you for requesting the Asset Stabilization Brief. I'd love to schedule a 15-minute conversation to walk you through how we can support your portfolio.\n\nIf next week works for you, here's my calendar: [Calendly link]\n\nLooking forward to it,\nShannon\nFounder, Moovin Forward LLC\n(817) 381-8831`)}`}
                    className="brief-btn brief-btn-primary"
                    data-testid={`brief-reply-${lead.id}`}
                  >
                    Reply
                  </a>
                  {Object.keys(STATUS_META).map(s => (
                    s !== status && (
                      <button
                        key={s}
                        onClick={() => updateStatus(lead, s, lead.notes || "")}
                        className="brief-btn brief-btn-status"
                        style={{ color: STATUS_META[s].color, borderColor: STATUS_META[s].color + "44" }}
                        data-testid={`brief-mark-${s}-${lead.id}`}
                      >
                        Mark {STATUS_META[s].label}
                      </button>
                    )
                  ))}
                  <button
                    onClick={() => { setEditingId(lead.id); setEditNotes(lead.notes || ""); }}
                    className="brief-btn brief-btn-secondary"
                    data-testid={`brief-edit-notes-${lead.id}`}
                  >
                    {lead.notes ? "Edit Notes" : "Add Notes"}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default BriefRequestsTab;
